<template>
  <div
    v-if="hasFeature('FEATURE_ALLERGEN_LIST') || preview"
    class="container"
  >
    <div
      v-show="!productsLoading && !loading"
      class="allergen-list"
      :class="{
        'allergen-list--scrollable': scrollLeft < scrollMax
      }"
      :style="`--allergens-length: ${allergens.length}; --allergens-scroll: -${scrollLeft}px; --allergens-header-height: ${headerHeight}px;`"
      @wheel="onMouseWheel"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
    >
      <div
        ref="allergenList"
        class="allergen-list__wrapper"
      >
        <header
          ref="allergenListHeader"
          class="allergen-list__header"
        >
          <div
            ref="allergenListFiller"
            class="allergen-list__col allergen-list__col--filler"
          >
            <h1
              class="allergen-list__title"
              v-html="$t('shoplist.product.details.allergens.title')"
            />
            <h2
              class="allergen-list__subtitle"
              v-html="locationName"
            />
          </div>
          <div
            v-for="allergen in allergens"
            :key="allergen"
            class="allergen-list__col allergen-list__col--header allergen-list__col--data"
            :class="{
              'allergen-list__col--active': activeAllergens.includes(allergen)
            }"
          >
            <span v-html="$t(`shoplist.product.details.allergens.${allergen}`)" />
          </div>
        </header>
        <section class="allergen-list__body">
          <template
            v-for="category in categoriesEnhancedSpread"
          >
            <div
              :key="category.category.code"
              class="allergen-list__category"
            >
              <h3
                v-if="category.category.name"
                class="allergen-list__col allergen-list__col--category"
                v-html="category.category.name"
              />
              <div
                v-for="allergen in allergens"
                :key="allergen"
                class="allergen-list__col allergen-list__col--data"
              />
            </div>
            <div
              v-for="product in category.products"
              :key="category.category.code + product.code"
              class="allergen-list__product"
              @mouseenter="onMouseEnter(product)"
              @mouseleave="onMouseLeave"
              @click="onClick(product)"
            >
              <div
                v-if="product.displayName"
                class="allergen-list__col allergen-list__col--product"
                :class="{
                  'allergen-list__col--active': activeProduct && activeProduct.code === product.code
                }"
                v-html="product.displayName"
              />
              <div
                v-for="allergen in allergens"
                :key="allergen"
                class="allergen-list__col allergen-list__col--data"
                :class="{
                  'allergen-list__col--active': activeProduct && activeProduct.code === product.code,
                  'allergen-list__col--contained': product.allergen && product.allergen.includes(allergen)
                }"
              />
            </div>
          </template>
        </section>
        <div class="allergen-list__scroller"/>
      </div>
      <footer class="allergen-list__footer">
        <div
          class="allergen-list__footer-info"
          v-html="$t('shoplist.product.details.allergens.additionalInfo')"
        />
        <Button
          level="tertiary"
          :text="$t('shoplist.product.details.allergens.pdfButton')"
          :href="pdfUrl"
          target="_blank"
          arrow
        />
      </footer>
    </div>
  </div>
  <div
    v-else
    class="container"
  >
    <h1 v-html="$t('shoplist.product.details.allergens.comingSoon')" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import GenericMixin from '@/mixins/GenericVueMixin'
import Button from '@/components/Button'
import { format } from '@/utils/format'
import { getUrlParams } from '@/utils/urlParams'

export default {
	mixins: [
		GenericMixin,
	],
  components: {
    Button,
  },
  props: {},
  data() {
    return {
      channel: this.$root.options.channel,
      locationName: this.$root.options.locationName,
      activeProduct: null,
      headerHeight: 0,
      scrollLeft: 0,
      scrollMax: 0,
      touchX: 0,
      touchY: 0,
      preview: false,
    }
  },
  computed: {
    ...mapGetters("productlist", [
			"products",
			"categories",
			"productsLoading",
      "allergens",
		]),
    ...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
    categoriesEnhanced() {
      return this.categories.reduce((categories, category) => {
        category = this.enhanceCategory(category)
				if (category) {
          categories.push(category)
        }
				return categories
			}, [])
    },
    categoriesEnhancedFlat() {
      return this.categoriesEnhanced.map(category => {
        return {
          category: category.category,
          products: this.flattenCategory(category)
        }
      })
    },
    categoriesEnhancedSpread() {
      return this.categoriesEnhanced
        .reduce((categories, category) => {
          return [
            ...categories,
            ...this.spreadCategories(category)
          ]
        }, [])
        .filter(category => category.products.length)
    },
    activeAllergens() {
      return this.activeProduct?.allergen ?? []
    },
    pdfUrl() {
      return format(this.themeValue('THEME_ALLERGEN_PDF_URL'), { language: this.language, channel: this.channel })
    }
  },
  methods: {
    enhanceCategory(category) {
      if (category.products?.length || category.children?.some(child => child.products?.length)) {
        const children = category.children?.reduce((children, child) => {
          child = this.enhanceCategory(child)
          if (child) {
            children.push(child)
          }
          return children
        }, []) || []
        const products = category.products?.reduce((a,p) => {
          const product = this.products.find(pp => pp.code === p)
          if (product) {
            a.push(...product.variants.map(variant => {
              return {
                ...variant,
                displayName: (() => {
                  let name = product.name;
                  if (variant.name && !product.name.includes(variant.name)) {
                    if (product.name === variant.name || variant.name.includes(product.name)) {
                      name = variant.name;
                    }
                    else {
                      name += ` ${variant.name}`;
                    }
                  }
                  return name
                })(),
              }
            }))
          }
          return a
        }, []) || []

        return {
          category,
          products,
          children,
        }
      }
      return null
    },
    flattenCategory(category) {
      return [
        ...category.products,
        ...category.children.map(child => this.flattenCategory(child)).flat()
      ]
    },
    spreadCategories(category) {
      return [
        category,
        ...category.children.map(this.spreadCategories).flat()
      ]
    },
    onClick(product) {
      if (this.isTouch) {
        if (this.activeProduct?.code === product.code) {
          this.activeProduct = null
        } else {
          this.activeProduct = product
        }
      }
    },
    onMouseEnter(product) {
      if (!this.isTouch) {
        this.activeProduct = product
      }
    },
    onMouseLeave() {
      if (!this.isTouch) {
        this.activeProduct = null
      }
    },
    onResize() {
      if (this.$refs.allergenList && this.$refs.allergenListFiller) {
        const filler = this.$refs.allergenListFiller
        const list = this.$refs.allergenList
        const fillerRect = filler.getBoundingClientRect()
        const listRect = list.getBoundingClientRect()
        const scrollWidth = list.scrollWidth
        const listWidth = listRect.width
        const fillerWidth = fillerRect.width
        const dataWidth = scrollWidth - fillerWidth
        const maxDataWidth = listWidth - fillerWidth
        const scrollMax = dataWidth - maxDataWidth

        this.scrollMax = scrollMax

        if (dataWidth + fillerWidth <= listWidth) {
          this.scrollMax = 0
          this.scrollLeft = 0
        }
      }
      if (this.$refs.allergenListHeader) {
        const headerRect = this.$refs.allergenListHeader.getBoundingClientRect()
        this.headerHeight = headerRect.height
      }
    },
    onMouseWheel(e) {
      if (e.deltaY === 0 && !e.ctrlKey) {
        e.preventDefault()
      }

      this.scrollList(e.deltaX)
    },
    onTouchStart(e) {
      this.touchX = e.changedTouches[0].screenX
      this.touchY = e.changedTouches[0].screenY
    },
    onTouchMove(e) {
      if (e.changedTouches.length === 1) {
        // if (Math.abs(this.touchY - e.changedTouches[0].screenY) === 0) {
        //   e.preventDefault()
        // }

        this.scrollList(this.touchX - e.changedTouches[0].screenX)
        this.touchX = e.changedTouches[0].screenX
      }
    },
    scrollList(x) {
      let scrollLeft = this.scrollLeft
      if (this.scrollMax > 0) {
        scrollLeft = Math.min(Math.max((scrollLeft + x), 0), this.scrollMax)
      } else {
        scrollLeft = 0;
      }
      this.scrollLeft = scrollLeft
    }
  },
  async mounted() {
    this.loading = true
    const params = getUrlParams()
    this.preview = params.preview === 'true'
    try {
      await this.$store.dispatch('productlist/loadCategories', { brand: this.brand, channel: this.channel, shippingCode: 'indining', getAll: true, accumulatorGroup: 'productListCategories' })
    } catch(err) {
      if (err.isMaintenanceError) {
        this.onMaintenanceMode()
      }
    }
    this.loading = false

    window.addEventListener('resize', this.onResize)
    this.onResize()
  },
  destroyed() {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>
